import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BillingAPI from 'dashboard/api/billing';

export function useBillingCheckout() {
  const { t } = useI18n();
  const isPaying = ref(false);

  const showKashierFeeNotice = data => {
    if (!data?.amount_egp || !data?.fee_egp) return;

    useAlert(
      t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_FEE_CHECKOUT_NOTICE', {
        amount: data.amount_egp,
        fee: data.fee_egp,
        rate: data.fee_rate_percent,
      })
    );
  };

  const redirectToKashir = async invoiceId => {
    const returnUrl = window.location.href;
    const { data } = await BillingAPI.createKashirCheckout({
      invoice_id: invoiceId,
      return_url: returnUrl,
      cancel_url: returnUrl,
    });
    if (data.checkout_url) {
      showKashierFeeNotice(data);
      window.location.href = data.checkout_url;
      return true;
    }
    useAlert(
      data.error || t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_UNAVAILABLE')
    );
    return false;
  };

  const payInvoice = async invoiceId => {
    isPaying.value = true;
    try {
      return await redirectToKashir(invoiceId);
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_UNAVAILABLE')
      );
      return false;
    } finally {
      isPaying.value = false;
    }
  };

  const addSeats = async ({
    limitKey,
    quantity,
    agentsQuantity = 0,
    inboxesQuantity = 0,
  }) => {
    isPaying.value = true;
    try {
      const payload =
        agentsQuantity > 0 || inboxesQuantity > 0
          ? {
              agents_quantity: agentsQuantity,
              inboxes_quantity: inboxesQuantity,
            }
          : { limit_key: limitKey, quantity };

      const { data } = await BillingAPI.createSeatAddition(payload);
      if (!data.invoice_id) {
        useAlert(
          data.error || t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR')
        );
        return false;
      }
      return await redirectToKashir(data.invoice_id);
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR')
      );
      return false;
    } finally {
      isPaying.value = false;
    }
  };

  const reduceSeats = async ({ limitKey, newLimit }) => {
    try {
      const { data } = await BillingAPI.reduceSeats({
        limit_key: limitKey,
        new_limit: newLimit,
      });
      if (data.error) {
        useAlert(data.error);
        return false;
      }
      useAlert(
        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEAT_REDUCTION_SCHEDULED')
      );
      return true;
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR')
      );
      return false;
    }
  };

  const purchaseAiCredits = async payAmount => {
    isPaying.value = true;
    try {
      const { data } = await BillingAPI.purchaseAiCredits(payAmount);
      if (!data.invoice_id) {
        useAlert(
          data.error || t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR')
        );
        return false;
      }
      if (data.amount_egp && data.fee_egp) {
        useAlert(
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_FEE_CHECKOUT_NOTICE', {
            amount: data.amount_egp,
            fee: data.fee_egp,
            rate: data.fee_rate_percent,
          })
        );
      }
      return await redirectToKashir(data.invoice_id);
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR')
      );
      return false;
    } finally {
      isPaying.value = false;
    }
  };

  return {
    isPaying,
    payInvoice,
    addSeats,
    reduceSeats,
    purchaseAiCredits,
  };
}
